'use client';

import { c as _c } from "react/compiler-runtime";
import { PAYMENT__DISCOUNT_SHAPE, type PaymentDiscountShape } from '@algoacademy/backend/modules/payment/enums';
import { Description, Field, Label } from '@algoacademy/design-system/web/forms/Fieldset';
import { Input, InputGroup } from '@algoacademy/design-system/web/forms/Input';
import { type ChangeEvent, type FocusEvent, useState } from 'react';
import { formatZloty } from '../../../_forms/MentoringOfferForm/_helpers/mentoring-terms';
import { MAXIMUM_DISCOUNT_PERCENT, MINIMUM_DISCOUNT_PERCENT, MINIMUM_DISCOUNT_ZLOTY, parseLiveDiscountFieldValue } from '../_helpers/discount-draft';
type DiscountValueFieldProps = {
  shape: PaymentDiscountShape;
  value: number | null;
  /** The złoty ceiling for the product configured above; ignored by the percent shape. */
  maximumZloty: number;
  /** What one billing period (invoice) of this product costs after the cadence discount above. */
  invoicePriceZloty: number;
  onChange: (value: number | null) => void;
};
type DiscountValueInputProps = {
  value: number | null;
  minimum: number;
  maximum: number;
  suffix: string;
  inputAriaLabel: string;
  onChange: (value: number | null) => void;
};
function DiscountValueInput(t0) {
  const $ = _c(22);
  const {
    value,
    minimum,
    maximum,
    suffix,
    inputAriaLabel,
    onChange
  } = t0;
  const [draftText, setDraftText] = useState(null);
  const isEditing = draftText !== null;
  const displayValue = isEditing ? draftText : value === null ? "" : String(value);
  let t1;
  if ($[0] !== value) {
    t1 = _event => {
      setDraftText(value === null ? "" : String(value));
    };
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleFocus = t1;
  let t2;
  if ($[2] !== maximum || $[3] !== minimum || $[4] !== onChange) {
    t2 = event => {
      const raw = event.target.value;
      const parsed = parseLiveDiscountFieldValue(raw, minimum, maximum);
      setDraftText(parsed === null ? raw : String(parsed));
      onChange(parsed);
    };
    $[2] = maximum;
    $[3] = minimum;
    $[4] = onChange;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleChange = t2;
  let t3;
  if ($[6] !== draftText || $[7] !== minimum || $[8] !== onChange || $[9] !== value) {
    t3 = _event_0 => {
      const raw_0 = draftText ?? (value === null ? "" : String(value));
      if (raw_0.trim() === "" || value === null) {
        onChange(minimum);
      }
      setDraftText(null);
    };
    $[6] = draftText;
    $[7] = minimum;
    $[8] = onChange;
    $[9] = value;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleBlur = t3;
  let t4;
  if ($[11] !== displayValue || $[12] !== handleBlur || $[13] !== handleChange || $[14] !== handleFocus || $[15] !== inputAriaLabel) {
    t4 = <Input type="number" step={1} value={displayValue} onFocus={handleFocus} onChange={handleChange} onBlur={handleBlur} aria-label={inputAriaLabel} className="font-medium tabular-nums" />;
    $[11] = displayValue;
    $[12] = handleBlur;
    $[13] = handleChange;
    $[14] = handleFocus;
    $[15] = inputAriaLabel;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== suffix) {
    t5 = <span data-slot="icon" className="pointer-events-none font-medium text-contentSecondary text-sm" aria-hidden="true">{suffix}</span>;
    $[17] = suffix;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t4 || $[20] !== t5) {
    t6 = <div data-slot="control" className="flex flex-1 items-end"><InputGroup className="w-full">{t4}{t5}</InputGroup></div>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
}

/**
 * How deep the discount goes. Percent and złoty share one field - label, hint line,
 * suffixed input - so staff reads both shapes the same way and only the unit changes.
 */
export function DiscountValueField(t0) {
  const $ = _c(20);
  const {
    shape,
    value,
    maximumZloty,
    invoicePriceZloty,
    onChange
  } = t0;
  const isPercent = shape === PAYMENT__DISCOUNT_SHAPE.PERCENT;
  const minimum = isPercent ? MINIMUM_DISCOUNT_PERCENT : MINIMUM_DISCOUNT_ZLOTY;
  const maximum = isPercent ? MAXIMUM_DISCOUNT_PERCENT : maximumZloty;
  let t1;
  if ($[0] !== invoicePriceZloty || $[1] !== isPercent || $[2] !== maximum || $[3] !== maximumZloty || $[4] !== minimum) {
    t1 = isPercent ? `${minimum}–${maximum}%` : `max ${formatZloty(maximumZloty)} · kwota regularna ${formatZloty(invoicePriceZloty)}`;
    $[0] = invoicePriceZloty;
    $[1] = isPercent;
    $[2] = maximum;
    $[3] = maximumZloty;
    $[4] = minimum;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const rangeHint = t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Label>Wysokość zniżki</Label>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== rangeHint) {
    t3 = <Description className="text-contentTertiary leading-5!">{rangeHint}</Description>;
    $[7] = rangeHint;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = isPercent ? "%" : "z\u0142";
  const t5 = isPercent ? "Zni\u017Cka procentowa od faktury" : "Zni\u017Cka kwotowa za jeden okres rozliczeniowy";
  let t6;
  if ($[9] !== maximum || $[10] !== minimum || $[11] !== onChange || $[12] !== shape || $[13] !== t4 || $[14] !== t5 || $[15] !== value) {
    t6 = <DiscountValueInput key={shape} value={value} minimum={minimum} maximum={maximum} suffix={t4} inputAriaLabel={t5} onChange={onChange} />;
    $[9] = maximum;
    $[10] = minimum;
    $[11] = onChange;
    $[12] = shape;
    $[13] = t4;
    $[14] = t5;
    $[15] = value;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t3 || $[18] !== t6) {
    t7 = <Field className="flex flex-col">{t2}{t3}{t6}</Field>;
    $[17] = t3;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}